import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { GatewayStatus } from '../types/gateway';
import type { WSClient } from '../ws/client';
import {
  fetchPagesProvisioningStatus,
  getManagedPagesGatewayState,
  installPagesProvisioningOnGateway,
  issuePagesProvisioningBundle,
  readGatewayConfigSnapshot,
  type ManagedPagesGatewayState,
  type PagesProvisioningStatus,
} from '../pages/provisioning';
import styles from './Settings.module.css';

interface PagesProvisioningSettingsProps {
  isOpen: boolean;
  onSignIn?: () => void;
  gatewayClient: WSClient | null;
  connectionState: GatewayStatus;
  activeGatewayName?: string | null;
  activeSessionKey?: string | null;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function requiresManualPagesGatewayAction(state: ManagedPagesGatewayState | null | undefined): boolean {
  return Boolean(
    state?.exists
    && state?.detectedServerName === state?.serverName
    && (!state?.managed || !state?.matchesUser),
  );
}

function getManualPagesGatewayActionMessage(
  state: ManagedPagesGatewayState | null | undefined,
  fallbackServerName: string,
): string {
  const serverName = state?.detectedServerName || state?.serverName || fallbackServerName;

  if (state?.managed) {
    return `The active gateway already uses ${serverName} for another user's Talkyn-managed Pages server. Talkyn will not overwrite it automatically. Choose a different server name or update the gateway manually.`;
  }

  return `The active gateway already uses ${serverName} for an unmanaged server. Talkyn will not overwrite it automatically. Choose a different server name or update the gateway manually.`;
}

export function PagesProvisioningSettings({
  isOpen,
  onSignIn,
  gatewayClient,
  connectionState,
  activeGatewayName,
  activeSessionKey,
}: PagesProvisioningSettingsProps) {
  const { isAuthenticated, user, getAuthHeaders } = useAuth();
  const [status, setStatus] = useState<PagesProvisioningStatus | null>(null);
  const [gatewayState, setGatewayState] = useState<ManagedPagesGatewayState | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [gatewayError, setGatewayError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pagesUrlFeedback, setPagesUrlFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [isPagesUrlCopied, setIsPagesUrlCopied] = useState(false);
  const pagesUrlCopiedResetTimeoutRef = useRef<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  const refreshState = useCallback(async () => {
    if (!isAuthenticated) {
      setStatus(null);
      setGatewayState(null);
      setStatusError(null);
      setGatewayError(null);
      return;
    }

    const nextStatus = await fetchPagesProvisioningStatus(getAuthHeaders());
    setStatus(nextStatus);
    setStatusError(null);

    if (connectionState === 'connected' && gatewayClient) {
      try {
        const snapshot = await readGatewayConfigSnapshot(gatewayClient);
        setGatewayState(getManagedPagesGatewayState({
          snapshot,
          serverName: nextStatus.gateway.serverName,
          userId: user?.id ?? null,
          pagesUrl: nextStatus.profile.pagesUrl,
          expectedCommand: nextStatus.gateway.command,
          expectedArgs: nextStatus.gateway.args,
          expectedCwd: nextStatus.gateway.cwd ?? null,
          expectedBaseUrl: nextStatus.provisioning.baseUrl,
        }));
        setGatewayError(null);
      } catch (error) {
        setGatewayState(null);
        setGatewayError(getErrorMessage(error));
      }
      return;
    }

    setGatewayState(null);
    setGatewayError(null);
  }, [connectionState, gatewayClient, getAuthHeaders, isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsRefreshing(true);
    setActionError(null);

    void refreshState()
      .catch((error) => {
        if (cancelled) return;
        setStatusError(getErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setIsRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, refreshState]);

  useEffect(() => {
    if (pagesUrlCopiedResetTimeoutRef.current !== null) {
      window.clearTimeout(pagesUrlCopiedResetTimeoutRef.current);
      pagesUrlCopiedResetTimeoutRef.current = null;
    }
    setIsPagesUrlCopied(false);
    setPagesUrlFeedback(null);
  }, [status?.profile.pagesUrl]);

  useEffect(() => () => {
    if (pagesUrlCopiedResetTimeoutRef.current !== null) {
      window.clearTimeout(pagesUrlCopiedResetTimeoutRef.current);
    }
  }, []);

  const isConnected = Boolean(
    gatewayState?.exists
    && gatewayState?.managed
    && gatewayState?.matchesUser
    && gatewayState?.matchesPagesUrl
    && gatewayState?.matchesRuntimeContract,
  );
  const hasGatewayMismatch = Boolean(gatewayState?.exists && !isConnected);
  const requiresManualGatewayAction = requiresManualPagesGatewayAction(gatewayState);
  const manualGatewayActionMessage = useMemo(
    () => (requiresManualGatewayAction
      ? getManualPagesGatewayActionMessage(gatewayState, status?.gateway.serverName || 'pages')
      : null),
    [gatewayState, requiresManualGatewayAction, status?.gateway.serverName],
  );
  const isGatewayReady = connectionState === 'connected' && Boolean(gatewayClient);

  const statusToneClass = useMemo(() => {
    if (gatewayError || actionError || statusError || hasGatewayMismatch) return styles.pagesStatusWarning;
    if (isConnected) return styles.pagesStatusConnected;
    if (!isAuthenticated || !isGatewayReady) return styles.pagesStatusIdle;
    return styles.pagesStatusIdle;
  }, [actionError, gatewayError, hasGatewayMismatch, isAuthenticated, isConnected, isGatewayReady, statusError]);

  const statusLabel = useMemo(() => {
    if (!isAuthenticated) return 'Sign in required';
    if (isRefreshing) return 'Checking Pages connection…';
    if (isInstalling) return 'Connecting Pages…';
    if (gatewayError || actionError || statusError || hasGatewayMismatch) return 'Gateway config needs attention';
    if (isConnected) return 'Connected';
    if (!isGatewayReady) return 'No gateway connected';
    return 'Ready to connect';
  }, [actionError, gatewayError, hasGatewayMismatch, isAuthenticated, isConnected, isGatewayReady, isInstalling, isRefreshing, statusError]);

  const primaryButtonLabel = useMemo(() => {
    if (!isAuthenticated) return 'Sign in to connect';
    if (isInstalling) return 'Connecting Pages…';
    if (requiresManualGatewayAction) return 'Manual gateway update required';
    if (!isGatewayReady) return 'Connect your AI first';
    if (isConnected) return 'Reconnect Pages';
    return 'Connect Pages to your AI';
  }, [isAuthenticated, isInstalling, isConnected, isGatewayReady, requiresManualGatewayAction]);

  const handleCopyPagesUrl = useCallback(async (pagesUrl: string) => {
    if (pagesUrlCopiedResetTimeoutRef.current !== null) {
      window.clearTimeout(pagesUrlCopiedResetTimeoutRef.current);
      pagesUrlCopiedResetTimeoutRef.current = null;
    }

    setPagesUrlFeedback(null);

    try {
      await navigator.clipboard.writeText(pagesUrl);
      setIsPagesUrlCopied(true);
      setPagesUrlFeedback({ tone: 'success', message: 'Copied Pages URL' });
      pagesUrlCopiedResetTimeoutRef.current = window.setTimeout(() => {
        setIsPagesUrlCopied(false);
        setPagesUrlFeedback((current) => (current?.tone === 'success' ? null : current));
        pagesUrlCopiedResetTimeoutRef.current = null;
      }, 2000);
    } catch {
      setIsPagesUrlCopied(false);
      setPagesUrlFeedback({ tone: 'error', message: 'Could not copy Pages URL' });
    }
  }, []);

  const handleProvision = useCallback(async () => {
    if (!isAuthenticated) {
      onSignIn?.();
      return;
    }
    if (!gatewayClient || connectionState !== 'connected' || !status) {
      return;
    }
    if (requiresManualGatewayAction && manualGatewayActionMessage) {
      setActionError(manualGatewayActionMessage);
      setSuccessMessage(null);
      return;
    }

    setIsInstalling(true);
    setActionError(null);
    setSuccessMessage(null);

    try {
      const snapshot = await readGatewayConfigSnapshot(gatewayClient);
      const preflightGatewayState = getManagedPagesGatewayState({
        snapshot,
        serverName: status.gateway.serverName,
        userId: user?.id ?? null,
        pagesUrl: status.profile.pagesUrl,
        expectedCommand: status.gateway.command,
        expectedArgs: status.gateway.args,
        expectedCwd: status.gateway.cwd ?? null,
        expectedBaseUrl: status.provisioning.baseUrl,
      });
      setGatewayState(preflightGatewayState);
      setGatewayError(null);

      if (requiresManualPagesGatewayAction(preflightGatewayState)) {
        throw new Error(getManualPagesGatewayActionMessage(preflightGatewayState, status.gateway.serverName));
      }

      const bundle = await issuePagesProvisioningBundle(getAuthHeaders());
      await installPagesProvisioningOnGateway(gatewayClient, bundle, {
        sessionKey: activeSessionKey,
      });
      setStatus((previous) => ({
        profile: bundle.profile,
        provisioning: {
          baseUrl: bundle.provisioning.baseUrl,
          label: bundle.provisioning.label,
          scopes: bundle.provisioning.scopes,
        },
        gateway: previous?.gateway
          ? { ...previous.gateway, serverName: bundle.gateway.serverName }
          : {
              serverName: bundle.gateway.serverName,
              transport: 'command',
            },
      }));
      setGatewayState(getManagedPagesGatewayState({
        snapshot: {
          config: {
            mcp: {
              servers: {
                [bundle.gateway.serverName]: bundle.gateway.serverConfig,
              },
            },
          },
        },
        serverName: bundle.gateway.serverName,
        userId: user?.id ?? null,
        pagesUrl: bundle.profile.pagesUrl,
        expectedCommand: typeof bundle.gateway.serverConfig.command === 'string'
          ? bundle.gateway.serverConfig.command
          : undefined,
        expectedArgs: Array.isArray(bundle.gateway.serverConfig.args)
          ? bundle.gateway.serverConfig.args as string[]
          : undefined,
        expectedCwd: typeof bundle.gateway.serverConfig.cwd === 'string'
          ? bundle.gateway.serverConfig.cwd
          : (typeof bundle.gateway.serverConfig.workingDirectory === 'string'
              ? bundle.gateway.serverConfig.workingDirectory
              : null),
        expectedBaseUrl: bundle.provisioning.baseUrl,
      }));
      setSuccessMessage('✅ Pages connected to your AI');
      void refreshState().catch(() => {});
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setIsInstalling(false);
    }
  }, [
    activeSessionKey,
    connectionState,
    gatewayClient,
    getAuthHeaders,
    isAuthenticated,
    manualGatewayActionMessage,
    onSignIn,
    refreshState,
    requiresManualGatewayAction,
    status,
    user?.id,
  ]);

  return (
    <div className={styles.pagesCard}>
      <div className={styles.pagesHeaderRow}>
        <div>
          <div className={styles.pagesTitle}>Pages</div>
          <div className={styles.pagesDescription}>
            Provision the active gateway via OpenClaw <code>mcp.servers</code>.
          </div>
        </div>
        <span className={`${styles.pagesStatusPill} ${statusToneClass}`}>{statusLabel}</span>
      </div>

      {status?.profile?.pagesUrl && (
        <div className={styles.pagesMetaBlock}>
          <div className={styles.pagesMetaLabel}>Pages URL</div>
          <div className={styles.pagesMetaValueRow}>
            <a
              className={`${styles.pagesMetaValue} ${styles.pagesMetaLink}`}
              href={status.profile.pagesUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {status.profile.pagesUrl}
            </a>
            <button
              type="button"
              className={`${styles.pagesMetaCopyButton} ${isPagesUrlCopied ? styles.pagesMetaCopyButtonSuccess : ''}`}
              onClick={() => void handleCopyPagesUrl(status.profile.pagesUrl)}
              aria-label={isPagesUrlCopied ? 'Pages URL copied' : 'Copy Pages URL'}
            >
              {isPagesUrlCopied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          {pagesUrlFeedback && (
            <div
              className={`${styles.pagesMetaFeedback} ${pagesUrlFeedback.tone === 'error'
                ? styles.pagesMetaFeedbackError
                : styles.pagesMetaFeedbackSuccess}`}
              role="status"
              aria-live="polite"
            >
              {pagesUrlFeedback.message}
            </div>
          )}
        </div>
      )}

      <div className={styles.pagesMetaGrid}>
        <div className={styles.pagesMetaItem}>
          <div className={styles.pagesMetaLabel}>Gateway</div>
          <div className={styles.pagesMetaValue}>{activeGatewayName || 'No active gateway'}</div>
        </div>
        <div className={styles.pagesMetaItem}>
          <div className={styles.pagesMetaLabel}>Server name</div>
          <div className={styles.pagesMetaValue}>{status?.gateway.serverName || 'pages'}</div>
        </div>
      </div>

      {!isAuthenticated && (
        <div className={styles.pagesHint}>
          Sign in first so Talkyn can issue a Pages provisioning bundle for your account.
        </div>
      )}

      {isAuthenticated && !isGatewayReady && (
        <div className={styles.pagesHint}>
          Connect your AI gateway first. The frontend installs Pages on the currently active gateway.
        </div>
      )}

      {isConnected && gatewayState?.metadata?.keyId && (
        <div className={styles.pagesHint}>
          This gateway already has the Talkyn-managed Pages server installed for your account.
        </div>
      )}

      {hasGatewayMismatch && (
        <div className={styles.pagesWarning}>
          {requiresManualGatewayAction ? (
            <>{manualGatewayActionMessage}</>
          ) : (
            <>
              The active gateway already has a server at <code>{gatewayState?.detectedServerName || status?.gateway.serverName || gatewayState?.serverName}</code>
              {gatewayState?.extraManagedServerNames?.length
                ? <> and extra managed entry{gatewayState.extraManagedServerNames.length > 1 ? 'ies' : 'y'} <code>{gatewayState.extraManagedServerNames.join(', ')}</code></>
                : null},
              but it does not match the current Talkyn Pages provisioning metadata/runtime contract. Reconnecting will update it.
            </>
          )}
        </div>
      )}

      {statusError && <div className={styles.pagesError}>{statusError}</div>}
      {gatewayError && <div className={styles.pagesError}>Gateway check failed: {gatewayError}</div>}
      {actionError && <div className={styles.pagesError}>{actionError}</div>}
      {successMessage && <div className={styles.pagesSuccess}>{successMessage}</div>}

      <div className={styles.pagesActions}>
        <button
          type="button"
          className={`${styles.pagesPrimaryButton} ${isInstalling ? styles.pagesPrimaryButtonBusy : ''}`}
          onClick={() => void handleProvision()}
          disabled={isInstalling || isRefreshing || requiresManualGatewayAction || (!isAuthenticated && !onSignIn)}
        >
          {primaryButtonLabel}
        </button>
        <button
          type="button"
          className={styles.pagesSecondaryButton}
          onClick={() => {
            setSuccessMessage(null);
            setActionError(null);
            setIsRefreshing(true);
            void refreshState()
              .catch((error) => setStatusError(getErrorMessage(error)))
              .finally(() => setIsRefreshing(false));
          }}
          disabled={isRefreshing || isInstalling || !isAuthenticated}
        >
          Refresh status
        </button>
      </div>
    </div>
  );
}
